import { CREATURE_BY_ID, GRAYS, PALETTE, TONE_COLORS } from "@eldrun/content";
import type { CreatureAttackEvent, HazardEvent } from "@eldrun/protocol";
import { Color, Mesh, PerspectiveCamera, PlaneGeometry, Scene, type WebGLRenderer } from "three";
import { FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import { bossSlam, CreatureFxPlayer, planCreatureFx } from "../game/creature-fx.ts";
import { fenixSource, FlameEmitters, hazardSource } from "../game/flame-emitters.ts";
import { HazardDecals } from "../game/hazards.ts";
import { SkillVfx, skillShock } from "../game/skill-vfx.ts";
import { Smoke } from "../game/smoke.ts";
import { applyAtmosphere, createAtmosphereUniforms, sampleAtmosphere } from "../render/atmosphere.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import { toonMaterial } from "../render/shaders/toon.ts";
import { gameColor } from "../render/style/roles.ts";

export interface FlamesShowcase {
  stats(): Record<string, unknown>;
  stop(): void;
}

export interface FlamesShowcaseOptions {
  /** Fase do dia (0 a 1). */
  phase: number;
  /** Instante fixo (s) no laço, para capturas; sem ele, em laço. */
  time: number | undefined;
  /** "Reduzir lampejos": as chamas sem tremeluzir, a magia sem cintilar e o brilho em 1. */
  steady: boolean;
  /** `&vista=perto`: a câmera perto do rastro de fogo e das brasas; `&vista=fenix`, perto do Círculo e do cajado. */
  view?: "perto" | "fenix" | undefined;
  /**
   * `&onda=1` (F94): a onda de choque do pós a cada volta, no Alto: a do Rochedo do Mapinguari (golpe pesado de
   * chefe, pelos dados) onde o cuspe cai e a da queda do Salto do Falcão no Círculo da Fênix.
   */
  shock?: boolean;
}

/** O laço da vitrine (s): os perigos, o fogo do Boitatá e a Fênix começam juntos a cada volta. */
const CYCLE = 4;
/** Passo fixo da simulação (s): a captura com `&instante` sai igual em qualquer cadência de quadros. */
const STEP = 1 / 60;
/** As ondas de choque da volta (`&onda=1`), em segundos depois do começo dela. */
const SHOCK_AT = [0.2, 1.2] as const;
const flat = () => 0;

/**
 * Vitrine das chamas e da magia (`?debug=chamas`, F93, sem servidor), no programa `fumaca`: à esquerda, o
 * rastro de fogo e as brasas com as lambidas; ao fundo, o cuspe, a chuva e o sopro do Boitatá no vermelho de
 * chefe; à direita, o Círculo da Fênix (7 m, como no jogo) na cor da Arcanista; na frente, as faíscas do acerto do cajado e a
 * fumaça de sempre, para comparar. Tudo recomeça a cada 4 s.
 */
export function startFlamesShowcase(renderer: WebGLRenderer, options: FlamesShowcaseOptions): FlamesShowcase {
  const scene = new Scene();
  const atmosphere = createAtmosphereUniforms();
  const sample = sampleAtmosphere(options.phase);
  scene.background = sample.horizon;
  const camera = new PerspectiveCamera(55, 1, 0.1, 300);
  camera.position.set(3.5, 7, 15);
  camera.lookAt(3.5, 0.5, -3);
  if (options.view === "perto") {
    camera.position.set(-3.2, 2, 3.2);
    camera.lookAt(-3.6, 0.7, -3.5);
  } else if (options.view === "fenix") {
    camera.position.set(10, 5.5, 8.5);
    camera.lookAt(10, 0.5, -3);
  }
  // Chão no cinza médio do mundo (a malha neon só usa a luminância).
  const ground = new Mesh(new PlaneGeometry(80, 80), toonMaterial(atmosphere, `#${new Color().setScalar(GRAYS.medio).getHexString()}`, 0));
  ground.rotation.x = -Math.PI / 2;
  scene.add(ground);
  const hazards = new HazardDecals(scene, atmosphere);
  const smoke = new Smoke(scene);
  smoke.reduceFlashes = options.steady;
  const emitters = new FlameEmitters(smoke);
  const vfx = new SkillVfx(scene);
  // Só as chamas do Boitatá: os estilhaços e as peças da tabela ficam de fora da vitrine.
  const fx = new CreatureFxPlayer({ shards: { burst: () => {} }, vfx: { spawnPieces: () => {} }, ground: flat, flames: smoke });
  const boss = gameColor(TONE_COLORS.chefe);
  const monster = gameColor(TONE_COLORS.inimigo);
  const arcanista = gameColor(PALETTE.classes.arcanista.hex);
  const boitata = CREATURE_BY_ID.get("boitata")!;
  const timer = new FrameTimer();
  const hud = new StatsHud(document.body);
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  /** Uma volta do laço: perigos, o fogo do Boitatá e a Fênix, todos a partir de `now` (ms). */
  const start = (now: number) => {
    const ms = CYCLE * 1000;
    const fire: HazardEvent = { id: "fogo", kind: "fogo", x: -6.5, z: 1.5, x2: -4.5, z2: -6, radius: 0.75, ms };
    const embers: HazardEvent = { id: "brasa", kind: "brasa", x: -2.2, z: -4.5, x2: -2.2, z2: -4.5, radius: 2.5, ms };
    for (const e of [fire, embers]) {
      hazards.spawn(e, now, flat);
      emitters.add(hazardSource(e, now, monster));
    }
    // O fogo do Boitatá: o cuspe e três marcas da chuva caindo uma depois da outra, e o sopro varrendo um cone.
    const attack = (id: string, x: number, z: number, yaw: number, inMs: number, mark?: number) => {
      const a = boitata.attacks.find((t) => t.id === id)!;
      const e: CreatureAttackEvent = { id: "boitata", attack: id, shape: a.shape, x, z, yaw, range: id === "sopro" ? 7 : a.range * 0.6, angle: a.angle, width: 0, inMs, ...(mark !== undefined ? { mark } : {}) };
      fx.play(planCreatureFx(e, boitata, now, 0, { color: boss, reduced: options.steady }));
    };
    attack("cuspe", 1.2, -5, 0, 150);
    for (let i = 0; i < 3; i++) attack("chuva", -0.6 + i * 1.6, -8.5, 0, 900 + i * 400, i);
    attack("sopro", 3.5, -9.5, Math.PI * 0.75, 400);
    // O Círculo da Fênix no tamanho do jogo (7 m): as peças da F78 e as chamas na borda, na cor da Arcanista.
    const fenix = { by: "", skill: "circulo-da-fenix", x: 10, z: -3, radius: 7, ms } as const;
    vfx.spawn(fenix, arcanista, flat, now);
    emitters.add(fenixSource("fenix", fenix, now, arcanista, 1));
  };

  let clock = 0;
  let lap = -1;
  // O relógio das ondas é o da vitrine (em passos fixos): a captura com `&instante` sai igual.
  const post = new PostFx(renderer, { clock: () => clock * 1000 });
  // Com a onda, o compositor nasce antes do primeiro quadro: com `&instante`, as voltas andam antes dele.
  if (options.shock) post.warm();
  const mapinguari = CREATURE_BY_ID.get("mapinguari")!;
  const rochedo = mapinguari.attacks.find((a) => a.id === "rochedo")!;
  const salto = skillShock({ skill: "salto-do-falcao", x: 10, z: -3, radius: 5, ms: 700 })!;
  let shocks = 0;
  let lastHit = -1;
  let lastPuff = -1;
  /** Anda a vitrine `dt` s (sempre em passos de `STEP`, pela mesma ordem do jogo). */
  const advance = (dt: number) => {
    clock += dt;
    const now = clock * 1000;
    const k = Math.floor(clock / CYCLE);
    if (k !== lap) {
      lap = k;
      shocks = 0;
      start(k * CYCLE * 1000);
    }
    // As ondas de choque (F94), cada uma no próprio instante da volta.
    const into = clock - lap * CYCLE;
    if (options.shock && shocks < SHOCK_AT.length && into >= SHOCK_AT[shocks]!) {
      if (shocks === 0) post.shockwave(1.2, 0.3, -5, bossSlam(mapinguari.archetype, rochedo), now, rochedo.range);
      else post.shockwave(10, salto.lift, -3, salto.strength, now, salto.radius);
      shocks++;
    }
    // O acerto do cajado a cada 0,6 s e a fumaça de sempre a cada 2 s, na frente.
    const hit = Math.floor(clock / 0.6);
    if (hit !== lastHit) {
      lastHit = hit;
      smoke.magic(2.2, 1.1, 3.5, arcanista, hit % 3 === 2 ? 9 : 6, hit % 3 === 2 ? 0.45 : 0.35);
    }
    const puff = Math.floor(clock / 2);
    if (puff !== lastPuff) {
      lastPuff = puff;
      smoke.burst(-1.2, 0.8, 3.5, 10, 0.8);
    }
    hazards.update(now);
    emitters.update(now, dt, camera.position.x, camera.position.z, flat);
    fx.update(now);
    vfx.update(now);
    smoke.update(dt);
  };

  const counter = new FrameStats(renderer.info);
  const unwatch = counter.watch(scene);
  post.bind(atmosphere);
  let last = performance.now();
  let owed = 0;
  renderer.setAnimationLoop((now: number) => {
    counter.begin();
    const frame = Math.min(0.1, (now - last) / 1000);
    timer.push(now - last);
    last = now;
    if (options.time === undefined) owed += frame;
    else if (clock < options.time) owed = options.time - clock;
    for (; owed >= STEP; owed -= STEP) advance(STEP);
    applyAtmosphere(atmosphere, sample, options.time ?? now / 1000);
    post.render(scene, camera, frame, counter);
    const f = counter.end();
    const s = smoke.stats;
    hud.update(now, () => [
      `Eldrun · chamas e magia · ${timer.fps.toFixed(0)} fps · draw calls ${f.drawCalls} (+${f.postCalls} do pós)`,
      `chamas ${s.flames} · magia ${s.magic} · fumaça ${s.smoke} · fontes ${emitters.count}${options.shock ? ` · ondas ${post.stats().waves}` : ""}${options.steady ? " · lampejos reduzidos" : ""}`,
    ]);
  });

  return {
    stats: () => ({
      fps: timer.fps,
      drawCalls: counter.last.drawCalls,
      programs: countPrograms(renderer.info.programs).scene,
      // Os nomes: o e2e confere que a fumaça, as chamas e a magia dividem um programa só (`fumaca`).
      programNames: (renderer.info.programs ?? []).map((p) => p.name),
      post: post.stats(counter.last),
      clock,
      flames: { ...smoke.stats, sources: emitters.count, emitted: emitters.emitted, fired: fx.fired },
    }),
    stop() {
      renderer.setAnimationLoop(null);
      post.dispose();
      unwatch();
      window.removeEventListener("resize", resize);
      hazards.dispose();
      vfx.dispose();
      emitters.clear();
      fx.clear();
      smoke.dispose();
      hud.remove();
      ground.geometry.dispose();
      (ground.material as { dispose(): void }).dispose();
    },
  };
}
